import type { CatalogTagOption } from "../client/svcContent";
import {
  languageDisplayNames,
  technologyDisplayNames,
  focusDisplayNames,
  difficultyDisplayNames,
} from "../constants/content";

export const capitalizeAllWords = (str: string): string =>
  str
    .split(/[\s_]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

export const getSelectedTagLabel = (categoryTags: CatalogTagOption[] = [], value: string): string =>
  categoryTags.find(tag => tag.id === value)?.displayName ?? value;

const FALLBACK_DISPLAY_NAME_MAPS: Record<string, Record<string, string>> = {
  languages: languageDisplayNames,
  technologies: technologyDisplayNames,
  focus: focusDisplayNames,
  difficulty: difficultyDisplayNames,
};

/**
 * Normalizes every tag entry into a common { id, displayName, description } shape
 * (matching CatalogTagOption), regardless of which tags API produced it: legacy
 * getTags() (plain string ids) or getTagsByTenant() (already in this shape, with
 * svc-content-provided display names/descriptions). This lets every consumer of
 * catalog tag data (Catalog.jsx, FilterPanel, TagBrowser) work with tags without
 * caring which endpoint supplied them.
 */
export const normalizeTagCategories = (
  data: Record<string, (string | CatalogTagOption)[]>
): Record<string, CatalogTagOption[]> =>
  Object.fromEntries(
    Object.entries(data).map(([category, values]) => {
      const fallbackMap = FALLBACK_DISPLAY_NAME_MAPS[category] || {};
      const normalized = (values || []).map(tag =>
        typeof tag === "string"
          ? { id: tag, displayName: fallbackMap[tag] || capitalizeAllWords(tag), description: null }
          : tag
      );
      return [category, normalized];
    })
  );
